<script lang="ts">
	import type { InputControl } from '$lib/components/atoms/input/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { UserAction } from '$lib/components/map/types';
	import type { AutocompleteLabel, Result } from '$lib/components/search/types';
	import type { Snippet } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import Input from '$lib/components/atoms/input/Input.svelte';
	import AutocompleteRaw from '$lib/components/search/autocomplete/AutocompleteLine.svelte';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { stopEvent } from '$lib/utils/event';
	import * as m from '$paraglide/messages';

	interface Props {
		placeholder?: string;
		ariaLabel?: string;
		id?: string;
		initialValue?: string;
		label?: string;
		disabled?: boolean;
		results: Result[] | null;
		defaultResults?: Result[] | null;
		icon?: IconName;
		controls?: InputControl[];
		class?: ClassValue;
		testid?: string;
		forceOpen?: boolean;
		children?: Snippet;
		resultItem?: Snippet<[{
			value: string;
			id: string;
			result: Result;
			resultIndex: number;
			activeItemIndex: number;
			isOpen: boolean;
			handleSelectItem: (result: Result, isFromStore: boolean) => void;
			onSelectItem?: (result: Result) => void;
			onmouseover: () => void;
		}]>;
		resultToLabel: (r: Result) => AutocompleteLabel;
		onclick?: () => void;
		onSelect?: (result: Result | null, userAction: UserAction | null, isFromStore: boolean) => void;
		onActivate?: () => void;
		onDeactivate?: () => void;
		onSearch?: (value: string) => void;
		onClearInput?: () => void;
		beforeListItems?: Snippet<[onmouseover: (index: number) => void, activeItemIndex: number, value: string]>;
	}

	const {
		placeholder = m.search__input__aria_label(),
		ariaLabel = m.search__input__aria_label(),
		id = 'search-input',
		disabled = false,
		resultToLabel,
		results,
		defaultResults = null,
		initialValue = '',
		icon = 'search',
		controls,
		label,
		onclick,
		class: classes,
		onSelect,
		onActivate,
		onDeactivate,
		onSearch,
		onClearInput,
		resultItem,
		testid,
		forceOpen = false,
		children,
		beforeListItems,
	}: Props = $props();

	let value = $derived(initialValue);
	let activeItemIndex = $state(-1);
	let list: HTMLElement;
	let isOpen = $state(false);
	let input: Input;
	let selected: Result | null = $state(null);

	const searchParams = getSearchParamsStore();

	let searchInputResults = $derived.by(() => {
		if (results?.length) {
			return results;
		}

		if (!value && defaultResults?.length) {
			return defaultResults;
		}

		return null;
	});
	const searchResults = $derived([...(searchInputResults ?? [])]);
	const shouldDisplayList = $derived(isOpen && (forceOpen || searchResults?.length));

	function loadResults({ target }: Event) {
		const val = (target as HTMLInputElement).value.trim();
		onSearch?.(val);
	}

	function select(result: Result | null, userAction: UserAction | null, isFromStore = false) {
		if (selected !== result || result === null) {
			onSelect?.(result, userAction, isFromStore);
		}
		selected = result;
	}

	function open() {
		if (!isOpen) {
			activeItemIndex = -1;
			isOpen = true;
		}
	}

	function close() {
		if (isOpen) {
			isOpen = false;
			activeItemIndex = -1;
		}
	}

	function activate() {
		isOpen = true;
		onActivate?.();
		open();
	}

	function deactivate() {
		// triggered by leaving the Input, which happens as well if the user selects an option
		// input blur happens before click, so before we hide the options we have to wait a bit
		// to ensure that selected value is updated to be the option the user selected by click
		setTimeout(() => {
			value = selected === null ? initialValue : resultToLabel(selected).label;
			close();
			onDeactivate?.();
		}, 300); // 300ms seems a bit high but firefox and windows laptops with tap gestures take a bit, see nvm-1901
	}

	function updateActiveItemIndex(delta: number) {
		const len = searchInputResults?.length;

		if (!len) {
			activeItemIndex = -1;
			return;
		}
		const targetIndex = activeItemIndex + delta;
		if (targetIndex < 0) {
			activeItemIndex = 0;
		} else if (targetIndex >= len) {
			activeItemIndex = len - 1;
		} else {
			activeItemIndex = targetIndex;
		}
		list
			?.querySelector(`li:nth-child(${activeItemIndex + 1})`)
			?.scrollIntoView({ behavior: 'instant', block: 'end', inline: 'nearest' });
	}

	function handleKeyboardSelection(e: KeyboardEvent) {
		const allResults = [...searchInputResults ?? []];

		if (!isOpen || !allResults.length) {
			return;
		}
		const { key } = e;
		switch (key) {
			case 'Escape':
				close();
				break;
			case 'Enter':
				if (activeItemIndex > -1) {
					select(allResults[activeItemIndex], 'enter');
				} else if (allResults.length > 0) {
					select(allResults[0], 'enter');
				}
				input.blur();
				break;
			case 'ArrowDown':
				updateActiveItemIndex(1);
				break;
			case 'ArrowUp':
				updateActiveItemIndex(-1);
				break;
			default:
				return; // not a key we handle
		}
		return stopEvent(e);
	}

	// uses mousedown event so it gets caught before input blur triggers deactivate
	// otherwise the option click would not be taken into account
	function handleSelectItem(result: Result, isFromStore?: boolean) {
		return select(result, 'click', isFromStore);
	}

	export function clear() {
		if (!$searchParams.selectedAddress) {
			value = '';
		}
	}

	export function getValue() {
		return value;
	}

	$effect(() => {
		if (value === '') {
			searchInputResults = defaultResults;
			onClearInput?.();
		}
	});

	function handleMouseOver(index: number) {
		activeItemIndex = index;
	}
</script>

<div class="search relative w-full min-w-0 {classes}">
	<Input
		{icon}
		{id}
		{placeholder}
		{ariaLabel}
		{disabled}
		{controls}
		{label}
		{onclick}
		bind:value
		bind:this={input}
		oninput={loadResults}
		onkeydown={handleKeyboardSelection}
		onActivate={activate}
		onDeactivate={deactivate}
	/>
	<ul
		class={['results rd-2 absolute z-hover w-full m-0 mt-1 p-0 shadow-smooth-bottom-2 bg-neutral-weakest text-coloredneutral text-md', shouldDisplayList && 'open']}
		role="listbox"
		aria-label={m.search_tools__results__aria_label()}
		bind:this={list}
	>
		{@render beforeListItems?.(handleMouseOver, activeItemIndex, value)}
		{#if searchResults?.length}
			{#each searchResults as result, resultIndex (resultIndex)}
				{#if resultItem}
					{@render resultItem({
						id,
						result,
						resultIndex,
						activeItemIndex,
						isOpen,
						handleSelectItem,
						value,
						onmouseover: () => handleMouseOver(resultIndex),
					})}
				{:else}
					<AutocompleteRaw
						{id}
						{testid}
						label={resultToLabel(result).label}
						highlighted={value}
						additionalLabel={resultToLabel(result).additionalLabel}
						active={resultIndex === activeItemIndex}
						isVisible={!isOpen}
						onmouseover={() => handleMouseOver(resultIndex)}
						onSelectItem={() => handleSelectItem(result)}
					/>
				{/if}
			{/each}
		{/if}
		{@render children?.()}
	</ul>
</div>

<style>
	.search {
		overflow: visible;
	}
	.results {
		display: flex;
		flex-direction: column;
		list-style: none;
		left: 0;
		right: 0;
		top: 100%;
		transition-duration: 0.3s;
		transition-property: max-height;
		transition-timing-function: ease-in;
		overflow: hidden;
		height: 0;
		max-height: 0;

		&.open {
			transition-timing-function: ease-out;
			height: auto;
			overflow-y: auto;
			max-height: 480px; /*12 x 40*/
			border-top-width: 1px;
			border-top-style: solid;
			--uno: border-neutral-soft;
			padding: 4px;
		}
	}

	.result-item {
		position: relative;
		cursor: pointer;
		overflow: hidden;
		text-overflow: ellipsis;
		flex-shrink: 0;
		white-space: nowrap;

		&.message {
			cursor: default;
			pointer-events: none;
			user-select: none;
		}
	}
</style>
